/**
 * Inserimento punteggio di tiro — live fire e dry fire.
 *
 * Pensato per essere compilato **al poligono, col telefono, in fretta**: celle
 * a tocco, una scheda per serie, e l'hit factor che si aggiorna mentre
 * compili.
 *
 * Regole che vengono dal dominio, non dall'estetica:
 *
 *  - **La division non si digita.** Si sceglie l'arma e la division si deriva
 *    dal suo setup per quella disciplina. È così che lo stesso tiratore non
 *    finisce registrato sotto division diverse per distrazione, rendendo
 *    incoerenti le proprie classifiche. Chi non ha armi configurate non è
 *    bloccato: il backend accetta una division indicata a mano.
 *  - **L'anteprima non è il punteggio.** Il valore mostrato qui arriva da
 *    `POST /api/shooting/score/preview` ed è solo per l'occhio; quello salvato
 *    lo ricalcola il backend a ogni scrittura. La formula vive in un posto solo.
 *  - **Il tempo si chiede solo dove entra nel punteggio.** Hit factor e
 *    time-plus lo usano; un sistema a soli punti (precisione, hit/miss) no, e
 *    chiederlo era una casella da ignorare — «vuole il tempo che non serve».
 *  - **Le schede nascono già pronte.** Se il coach ha previsto 5 serie, se ne
 *    trovano 5, etichettate con la loro distanza; le vuote non si salvano.
 *  - **Senza sistema di punteggio non ci si ferma.** Il drill dovrebbe averne
 *    uno, ma se manca il tiratore lo sceglie qui invece di non poter
 *    registrare niente: la scheda a bersagli senza zone era una griglia vuota
 *    e sembrava rotta.
 *
 * Spec: docs/shooting/02-domain-model.md §6-7 · 04-frontend-ux.md §2 ·
 *       15-seconda-sessione-federico.md
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { fetchAPI } from "../../lib/utils";
import { firearmTypeIcon } from "./weaponIcons";
import { TimeField } from "./TimeField";
import {
	TargetCardStatusLine,
	TargetScoreCard,
	scorableTargets,
	targetCardStatus,
} from "./TargetScoreCard";
import {
	metricNeedsTime,
	plannedSeriesCount,
	plannedSeriesList,
	seriesDescription,
	seriesHeading,
} from "./shootingPlan";

/** Le configurazioni dell'arma, con fallback sui vecchi campi piatti. */
const setupsOf = (firearm) => {
	if (Array.isArray(firearm?.setups) && firearm.setups.length)
		return firearm.setups;
	if (!firearm?.discipline) return [];
	return [
		{
			discipline: firearm.discipline,
			division: firearm.division || null,
			power_factor: firearm.power_factor || null,
			is_default: !!firearm.is_default,
		},
	];
};

const newSeries = (index, planned) => ({
	series_index: index + 1,
	distance_m: planned?.distance_m ?? null,
	label: planned?.label || null,
	time_seconds: "",
	targets: [],
	hits: {},
	penalties: {},
});

const formatScore = (value, digits = 2) =>
	value === null || value === undefined ? "—" : Number(value).toFixed(digits);

export function ShootingScoreForm({ prescription = {}, value, onChange }) {
	const mode = prescription.mode === "dry_fire" ? "dry_fire" : "live_fire";
	// Formato libero: non c'è punteggio da calcolare. Si registra com'è andata,
	// e l'autovalutazione è il dato che rende il risultato salvabile.
	const isFreeFormat = prescription.prescription_format === "free";
	const [firearms, setFirearms] = useState([]);
	const [reference, setReference] = useState({
		divisions: [],
		scoring_systems: [],
	});
	const [preview, setPreview] = useState(null);
	const [previewing, setPreviewing] = useState(false);

	const data = value || {};
	// `strings` è il nome storico della lista: si legge ancora, perché un
	// risultato salvato prima della rinomina ce l'ha.
	const series = Array.isArray(data.series)
		? data.series
		: Array.isArray(data.strings)
			? data.strings
			: [];
	const times = Array.isArray(data.times) ? data.times : [];
	// Le serie che il coach ha previsto: l'atleta trova le schede già
	// etichettate («5 m», «10 m») invece di doversi ricordare l'ordine.
	const plannedSeries = plannedSeriesList(prescription);
	const plannedCount = plannedSeriesCount(prescription);
	// Lo stage ha bersagli posizionati? Allora si scora bersaglio per
	// bersaglio. Un drill descritto a tabella resta sulla griglia a zone.
	const hasTargetCard = scorableTargets(prescription.targets).length > 0;
	// Il sistema di punteggio: quello del drill, o quello scelto qui sotto
	// quando il drill non ce l'ha.
	const systemId = prescription.scoring_system_id || data.scoring_system_id || null;

	const patch = useCallback(
		(changes) => onChange?.({ ...data, mode, ...changes }),
		// `data` cambia a ogni battuta: la dipendenza va sul contenuto, non
		// sull'identità dell'oggetto, o l'effetto di anteprima gira a vuoto.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(data), mode, onChange],
	);

	useEffect(() => {
		let cancelled = false;
		Promise.all([
			fetchAPI("/profile/firearms").catch(() => []),
			fetchAPI("/shooting/reference").catch(() => ({})),
		]).then(([list, ref]) => {
			if (cancelled) return;
			setFirearms(Array.isArray(list) ? list : []);
			setReference({
				divisions: ref?.divisions || [],
				scoring_systems: ref?.scoring_systems || [],
			});
		});
		return () => {
			cancelled = true;
		};
	}, []);

	// Le schede previste nascono già pronte. Una volta sola: se il tiratore
	// le cancella tutte, non ricompaiono da sole.
	const didSeed = useRef(false);
	useEffect(() => {
		if (didSeed.current || isFreeFormat || mode !== "live_fire") return;
		if (series.length > 0 || plannedCount === 0) return;
		didSeed.current = true;
		patch({
			series: Array.from({ length: plannedCount }, (_, i) =>
				newSeries(i, plannedSeries[i]),
			),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode, isFreeFormat, series.length, plannedCount]);

	// Zone e penalità dal sistema di punteggio: A/C/D per l'IPSC, HIT/M per un
	// drill hit-miss. Non sono cablate nel form.
	const system = reference.scoring_systems.find((s) => s.system_id === systemId);
	const zones = system?.zones || [];
	const penalties = system?.penalties || [];
	const metric = system?.metric || "hit_factor";
	const needsTime = metricNeedsTime(metric);

	// La disciplina si deriva dal **sistema di punteggio** del drill, che è
	// per disciplina: è lo stesso ragionamento che fa il backend quando
	// congela division e classe. Nel dry fire non c'è sistema, quindi non si
	// filtra per disciplina e si mostrano tutte le armi.
	const discipline = system?.discipline || null;

	// Armi compatibili: quelle configurate per questa disciplina e, se il drill
	// specifica l'arma, del tipo giusto. Su un drill di pistola non si propone
	// la carabina.
	const usable = useMemo(
		() =>
			firearms.filter((firearm) => {
				if (firearm.is_active === false) return false;
				if (
					prescription.firearm_type &&
					(firearm.firearm_type || "handgun") !== prescription.firearm_type
				) {
					return false;
				}
				if (!discipline) return true;
				return setupsOf(firearm).some((s) => s.discipline === discipline);
			}),
		[firearms, prescription.firearm_type, discipline],
	);

	const selected = usable.find((f) => f.firearm_id === data.firearm_id) || null;
	const selectedSetup = selected
		? setupsOf(selected).find((s) => s.discipline === discipline) ||
			setupsOf(selected)[0]
		: null;

	// Preselezione: la preferita per questa disciplina, o l'unica disponibile.
	const didPreselect = useRef(false);
	useEffect(() => {
		if (didPreselect.current || data.firearm_id || usable.length === 0) return;
		const preferred =
			usable.find((f) =>
				setupsOf(f).some((s) => s.discipline === discipline && s.is_default),
			) || (usable.length === 1 ? usable[0] : null);
		if (preferred) {
			didPreselect.current = true;
			patch({ firearm_id: preferred.firearm_id });
		}
	}, [usable, discipline, data.firearm_id, patch]);

	// ── Anteprima: debounced, e non è il valore salvato ──
	const previewKey = JSON.stringify({
		mode,
		series,
		times,
		pf: selectedSetup?.power_factor,
	});
	useEffect(() => {
		if (mode === "live_fire" && !systemId) return;
		const hasContent =
			mode === "dry_fire" ? times.length > 0 : series.length > 0;
		if (!hasContent) {
			setPreview(null);
			return;
		}
		const timer = setTimeout(async () => {
			setPreviewing(true);
			try {
				const body =
					mode === "dry_fire"
						? {
								mode: "dry_fire",
								times: times.map(Number).filter((n) => !Number.isNaN(n)),
								par_time_seconds: prescription.par_time_seconds ?? null,
							}
						: {
								mode: "live_fire",
								scoring_system_id: systemId,
								power_factor: selectedSetup?.power_factor || null,
								series_scoring: prescription.series_scoring || "individual",
								series: series.map((s, i) => ({
									series_index: i + 1,
									distance_m: s.distance_m ?? null,
									label: s.label || null,
									time_seconds: Number(s.time_seconds) || 0,
									targets: s.targets || [],
									hits: s.hits || {},
									penalties: s.penalties || {},
								})),
							};
				const result = await fetchAPI("/shooting/score/preview", {
					method: "POST",
					body: JSON.stringify(body),
				});
				setPreview(result);
			} catch {
				// Un'anteprima che fallisce non deve bloccare l'inserimento:
				// il punteggio vero lo calcola il backend al salvataggio.
				setPreview(null);
			} finally {
				setPreviewing(false);
			}
		}, 400);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [previewKey, systemId, prescription.par_time_seconds]);

	const updateSeries = (index, changes) =>
		patch({
			series: series.map((s, i) => (i === index ? { ...s, ...changes } : s)),
		});

	const setCount = (index, group, code, raw) => {
		const current = series[index]?.[group] || {};
		const next = { ...current };
		const parsed = parseInt(raw, 10);
		if (!raw || Number.isNaN(parsed) || parsed <= 0) delete next[code];
		else next[code] = parsed;
		updateSeries(index, { [group]: next });
	};

	/**
	 * Una serie nuova, etichettata con la riga di prescrizione che le tocca.
	 *
	 * È ciò che chiude il caso del lento mirato: il coach ha scritto 5, 10 e
	 * 15 metri, e l'atleta si trova le tre schede pronte invece di dover
	 * ricordare l'ordine e scriverlo a mano.
	 */
	const addSeries = () =>
		patch({
			series: [...series, newSeries(series.length, plannedSeries[series.length])],
		});

	const WeaponIcon = firearmTypeIcon(
		selected?.firearm_type || prescription.firearm_type || "handgun",
	);

	/** Il numero di una serie, nella metrica del sistema. */
	const seriesLine = (seriePreview) => {
		if (!seriePreview) return null;
		if (metric === "time_plus")
			return `${formatScore(seriePreview.final_time_seconds)}s · ${seriePreview.points} punti`;
		if (metric === "points") return `${seriePreview.points} punti`;
		return `${seriePreview.points} punti · HF ${formatScore(seriePreview.hit_factor)}`;
	};

	if (isFreeFormat) {
		return (
			<div className="space-y-2">
				<Label className="label-tactical">Com'è andata</Label>
				<div className="grid grid-cols-5 gap-1.5">
					{[1, 2, 3, 4, 5].map((rating) => (
						<button
							key={rating}
							type="button"
							onClick={() =>
								onChange?.({
									...data,
									mode,
									prescription_format: "free",
									self_rating: rating,
								})
							}
							className={`h-12 rounded-sm border text-base font-heading transition-colors ${
								data.self_rating === rating
									? "border-volt bg-volt/15 text-volt"
									: "border-border text-muted-foreground hover:border-volt/40"
							}`}
						>
							{rating}
						</button>
					))}
				</div>
				<p className="text-xs text-muted-foreground">
					1 = male, 5 = ottimo. Aggiungi quello che vuoi nelle note.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{/* ── Arma: la division si deriva, non si digita ── */}
			{usable.length > 0 ? (
				<div className="space-y-1.5">
					<Label className="label-tactical flex items-center gap-1.5">
						<WeaponIcon size={14} /> Arma
					</Label>
					<Select
						value={data.firearm_id || "__none__"}
						onValueChange={(v) =>
							patch({ firearm_id: v === "__none__" ? null : v })
						}
					>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm h-11">
							<SelectValue placeholder="Scegli l'arma" />
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							<SelectItem value="__none__">Non indicata</SelectItem>
							{usable.map((firearm) => (
								<SelectItem key={firearm.firearm_id} value={firearm.firearm_id}>
									{firearm.nickname}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{selectedSetup && (
						<p className="text-xs text-muted-foreground">
							{selectedSetup.division
								? reference.divisions.find(
										(d) =>
											d.value === selectedSetup.division &&
											d.discipline === selectedSetup.discipline,
									)?.label || selectedSetup.division
								: "Nessuna division"}
							{selectedSetup.power_factor
								? ` · ${selectedSetup.power_factor === "major" ? "Major" : "Minor"}`
								: ""}
						</p>
					)}
				</div>
			) : (
				<p className="text-xs text-muted-foreground">
					Nessuna arma configurata per questa disciplina. Puoi registrare
					comunque il punteggio; configura le tue armi dal profilo per avere la
					division automatica.
				</p>
			)}

			{mode === "dry_fire" ? (
				/* ── DRY FIRE: solo tempi ── */
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<Label className="label-tactical">
							Tempi
							{prescription.par_time_seconds
								? ` · par ${prescription.par_time_seconds}s`
								: ""}
						</Label>
						<Button
							type="button"
							size="sm"
							variant="outline"
							className="rounded-sm h-8"
							onClick={() => patch({ times: [...times, ""] })}
						>
							<Plus className="w-3.5 h-3.5 mr-1" /> Ripetizione
						</Button>
					</div>
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
						{times.map((time, index) => (
							<div key={`t_${index}`} className="flex items-center gap-0.5">
								<TimeField
									compact
									value={time}
									onChange={(next) =>
										patch({
											times: times.map((t, i) => (i === index ? next : t)),
										})
									}
								/>
								<button
									type="button"
									onClick={() =>
										patch({ times: times.filter((_, i) => i !== index) })
									}
									className="shrink-0 px-0.5 text-muted-foreground hover:text-red-500"
								>
									<Trash2 className="w-3 h-3" />
								</button>
							</div>
						))}
					</div>
					<div className="space-y-1.5">
						<Label className="label-tactical">Autovalutazione (1-5)</Label>
						<Select
							value={data.self_rating ? String(data.self_rating) : "__none__"}
							onValueChange={(v) =>
								patch({ self_rating: v === "__none__" ? null : Number(v) })
							}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm h-11">
								<SelectValue placeholder="—" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="__none__">Non indicata</SelectItem>
								{[1, 2, 3, 4, 5].map((n) => (
									<SelectItem key={n} value={String(n)}>
										{n}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
			) : (
				/* ── LIVE FIRE: una scheda per serie ── */
				<div className="space-y-2">
					{/* Il drill dovrebbe avere il suo sistema di punteggio. Se non
					    ce l'ha, il tiratore lo sceglie qui: fermarlo con un avviso
					    lasciava una scheda senza zone, che sembrava rotta. */}
					{!prescription.scoring_system_id && (
						<div className="space-y-1.5 rounded-sm border border-amber-400/40 bg-amber-400/10 p-2">
							<Label className="label-tactical text-amber-400">
								Sistema di punteggio
							</Label>
							<p className="text-[11px] text-muted-foreground">
								Il drill non ne ha uno: scegli con quale tabella registrare.
							</p>
							<Select
								value={data.scoring_system_id || "__none__"}
								onValueChange={(v) =>
									patch({ scoring_system_id: v === "__none__" ? null : v })
								}
							>
								<SelectTrigger className="bg-background border-transparent rounded-sm h-11">
									<SelectValue placeholder="Scegli" />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="__none__">—</SelectItem>
									{reference.scoring_systems.map((s) => (
										<SelectItem key={s.system_id} value={s.system_id}>
											{s.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{/* Cosa il coach ha chiesto di registrare. Sta in cima perché
					    va letto PRIMA di compilare — è il caso del brandeggio, in
					    cui si segna solo lo split. */}
					{prescription.scoring_instructions && (
						<p className="text-xs rounded-sm border border-volt/30 bg-volt/[0.08] px-2 py-1.5">
							<span className="text-muted-foreground">Da registrare: </span>
							{prescription.scoring_instructions}
						</p>
					)}

					{series.map((serie, index) => {
						const seriePreview = (preview?.series || preview?.strings)?.[index];
						const planned = plannedSeries[index];
						// Distanza ed etichetta insieme: è la consegna di quella
						// serie («10 m · da sinistra a destra»), e va letta prima
						// di sparare, non indovinata da un titolo troncato.
						const description = seriesDescription(serie, planned);
						const status = hasTargetCard
							? targetCardStatus(prescription.targets, serie.targets)
							: null;

						return (
							<div
								key={`serie_${index}`}
								className="rounded-sm border border-border bg-secondary/40 p-2.5 space-y-2"
							>
								<div className="flex items-center gap-2">
									<span className="text-xs font-semibold shrink-0 min-w-[2.5rem] text-muted-foreground">
										Serie {index + 1}
									</span>
									{/* Il tempo solo dove entra nel punteggio. */}
									{needsTime ? (
										<TimeField
											value={serie.time_seconds}
											onChange={(next) =>
												updateSeries(index, { time_seconds: next })
											}
										/>
									) : (
										<div className="flex-1" />
									)}
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="rounded-sm h-9 w-9 shrink-0 hover:text-red-500"
										onClick={() =>
											patch({ series: series.filter((_, i) => i !== index) })
										}
									>
										<Trash2 className="w-4 h-4" />
									</Button>
								</div>
								{description && (
									<p className="text-sm font-medium text-volt leading-snug break-words">
										{description}
									</p>
								)}

								{zones.length === 0 ? (
									<p className="text-[11px] text-muted-foreground">
										Scegli il sistema di punteggio per compilare la scheda.
									</p>
								) : hasTargetCard ? (
									<>
										<TargetScoreCard
											targets={prescription.targets}
											zones={zones}
											penalties={penalties}
											value={serie.targets || []}
											onChange={(rows) => updateSeries(index, { targets: rows })}
											courseType={prescription.course_type}
										/>
										<TargetCardStatusLine status={status} />
									</>
								) : (
									/* Nessun bersaglio posizionato: si resta sulla griglia a
									   zone aggregate, o un drill descritto a tabella non
									   sarebbe compilabile. */
									<div
										className="grid gap-1"
										style={{
											gridTemplateColumns: `repeat(${Math.min(
												zones.length + penalties.length,
												8,
											)}, minmax(0, 1fr))`,
										}}
									>
										{zones.map((zone) => (
											<div key={zone.code}>
												<p className="text-[10px] text-center text-muted-foreground">
													{zone.code}
												</p>
												<Input
													type="number"
													inputMode="numeric"
													min="0"
													value={serie.hits?.[zone.code] ?? ""}
													onChange={(e) =>
														setCount(index, "hits", zone.code, e.target.value)
													}
													className="bg-background border-transparent rounded-sm h-11 text-center text-base px-0"
													placeholder="0"
												/>
											</div>
										))}
										{penalties.map((penalty) => (
											<div key={penalty.code}>
												<p className="text-[10px] text-center text-red-400/80">
													{penalty.code}
												</p>
												<Input
													type="number"
													inputMode="numeric"
													min="0"
													value={serie.penalties?.[penalty.code] ?? ""}
													onChange={(e) =>
														setCount(
															index,
															"penalties",
															penalty.code,
															e.target.value,
														)
													}
													className="bg-background border-transparent rounded-sm h-11 text-center text-base px-0"
													placeholder="0"
												/>
											</div>
										))}
									</div>
								)}

								{seriePreview && (
									<p className="text-xs text-volt">{seriesLine(seriePreview)}</p>
								)}
							</div>
						);
					})}

					<Button
						type="button"
						variant="outline"
						className="w-full rounded-sm h-11"
						onClick={addSeries}
					>
						<Plus className="w-4 h-4 mr-1.5" />
						{seriesHeading(null, plannedSeries[series.length])
							? `Aggiungi serie · ${seriesHeading(null, plannedSeries[series.length])}`
							: "Aggiungi serie"}
					</Button>
					{plannedCount > series.length && (
						<p className="text-[11px] text-muted-foreground px-1">
							{plannedCount} serie previste dal coach, {series.length} registrate.
						</p>
					)}
				</div>
			)}

			{/* ── Totale: anteprima, non il valore salvato ── */}
			{(preview || previewing) && (
				<div className="rounded-sm border border-volt/25 bg-volt/[0.06] px-3 py-2">
					{previewing && !preview ? (
						<p className="text-xs text-muted-foreground flex items-center gap-1.5">
							<Loader2 className="w-3 h-3 animate-spin" /> Calcolo...
						</p>
					) : mode === "dry_fire" ? (
						<p className="text-sm">
							<span className="font-semibold text-volt">
								{formatScore(preview.best_time_seconds)}s
							</span>{" "}
							<span className="text-muted-foreground">
								migliore · media {formatScore(preview.avg_time_seconds)}s
								{preview.success_rate_pct !== null &&
								preview.success_rate_pct !== undefined
									? ` · ${preview.success_rate_pct.toFixed(0)}% dentro il par`
									: ""}
							</span>
						</p>
					) : (
						<>
							{/* Il numero in evidenza dice **cosa** è: l'etichetta la
							    produce il backend insieme al calcolo, così la parola e
							    il numero non possono divergere. Il numero cambia con
							    la metrica: hit factor, tempo, o punti. */}
							{preview.total_label && (
								<p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
									{preview.total_label}
								</p>
							)}
							<p className="text-sm">
								<span className="font-semibold text-volt">
									{metric === "time_plus"
										? `${formatScore(preview.best_score ?? preview.total_final_time_seconds)}s`
										: metric === "points"
											? `${formatScore(preview.best_score ?? preview.total_points, 0)} punti`
											: `HF ${formatScore(preview.hit_factor)}`}
								</span>{" "}
								<span className="text-muted-foreground">
									{preview.series_count > 1
										? `· media ${formatScore(preview.avg_score, metric === "points" ? 1 : 2)} `
										: ""}
									· {preview.total_points} punti in totale
									{needsTime
										? ` · ${formatScore(preview.total_time_seconds)}s`
										: ""}
									{preview.accuracy_pct !== null &&
									preview.accuracy_pct !== undefined
										? ` · ${preview.accuracy_pct.toFixed(0)}% zona migliore`
										: ""}
								</span>
							</p>
						</>
					)}
				</div>
			)}
		</div>
	);
}
